"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.KIRANA_POP_THEME = void 0;
const common_1 = require("./common");
exports.KIRANA_POP_THEME = (0, common_1.firstPartyTheme)({
    id: 'kirana-pop',
    name: 'Kirana Pop',
    tagline: 'Bold Indian D2C retail: chilli red, rani pink and haldi cream, loud in the best way.',
    description: 'Kirana Pop is built for Indian direct-to-consumer brands that sell snacks, masalas, skincare, apparel and gifting. Warm haldi cream ground, a chilli-red call to action, rani-pink highlights and chunky Bricolage Grotesque headlines over a friendly Plus Jakarta Sans. Sticker badges, price-forward cards, festive energy and a checkout flow that feels as easy as ordering from the corner shop. Works for INR pricing, COD, combos and WhatsApp-first audiences.',
    categories: ['retail', 'bold', 'playful'],
    tokens: {
        colors: {
            background: '#fff8ec',
            surface: '#ffebc7',
            text: '#2a1406',
            muted: '#7a4e2d',
            primary: '#d62e14',
            primaryText: '#ffffff',
            accent: '#e0197d',
            border: '#f0d3a6',
        },
        fonts: { heading: 'Bricolage Grotesque', body: 'Plus Jakarta Sans' },
        radius: 14,
        density: 'comfortable',
        typeScale: 1.333,
    },
    guide: `## Mood
The energy of a Diwali market and the trust of your neighbourhood kirana, in a modern D2C store. Warm, loud, generous, a little cheeky. The customer should feel invited, never lectured. Think packaging you'd keep: bold colour blocks, stickers, hand-drawn flourishes, big honest prices.

## Grid and layout
- Mobile first, always: most buyers arrive from Instagram or WhatsApp on a phone. Design the 390px layout before the desktop one.
- Dense but tidy: a 2-column product grid on mobile, 4 on desktop, 12–16px gaps. Products are the hero of the home page, above the fold on mobile.
- Colour-blocked sections: alternate the cream background with surface (deeper haldi) bands and one full chilli-red band for the offer or combo. Each band has a bold headline and a sticker.
- Sticky bottom bar on mobile product pages with price and the add-to-cart button.

## Typography
- Headlines in Bricolage Grotesque, weight 700–800, tight leading, sometimes rotated -2deg inside a sticker. Mix Hinglish naturally if the brand does ("Ghar ka swaad, ab har ghar").
- Body in Plus Jakarta Sans, 15–16px, weight 500 for UI, 400 for long text.
- Prices are big and bold: the 2xl step, weight 800, with the MRP struck through in muted text beside it and a "% off" sticker.

## Colour
- Chilli red (primary) is for buying: add to cart, buy now, checkout. Nothing else is red.
- Rani pink (accent) is for delight: stickers, "bestseller" and "new" badges, underline scribbles, the wishlist heart.
- Text is deep tamarind brown, never pure black. Borders are soft caramel.
- One full-bleed red band per page maximum.

## Imagery
Bright, high-key product photography on solid colour backdrops (cream, pink, turmeric) with hard shadows. Ingredients scattered around the pack. Real hands, real kitchens, real festivals. Product cards use 1:1 images with a rounded radius matching the theme. Avoid moody, dark or desaturated photos.

## Motion
Snappy and playful: 150–250ms, ease-out with a slight overshoot on stickers and add-to-cart confirmations (scale 0.95 to 1.05 to 1). Cart count badge pops. Marquee strips for offers ("Free shipping above ₹499 · COD available · Made in India") scrolling slowly. Never slow fades on product UI.

## Copy tone
Warm, direct, fun. Second person, short lines, real benefits: "No palm oil. No preservatives. Just Nani's recipe." Lead with the offer and the trust signals: COD available, free shipping threshold, easy returns, ratings count. Use ₹ and Indian number formatting (₹1,299, 1.2 lakh happy customers).

## Do
- Put trust badges (COD, free shipping, returns, made in India) right under the hero and in the cart.
- Show combos and bundles as their own section with a savings sticker.
- Make WhatsApp the support channel if the brand uses it.

## Don't
- No minimalist grey luxury look, no thin weights, no tiny prices.
- Don't use red for anything that isn't a purchase action.
- Don't hide prices behind "view details".`,
    sections: [
        {
            name: 'Hero: offer stack',
            recipe: 'Mobile: a cream section with a sticker (rani pink, rotated -4deg) reading the current offer, a two-line Bricolage headline, one sentence, and a chilli-red "Shop bestsellers" button full width. Below it, a horizontally scrolling row of 3–4 bestseller cards so products appear above the fold. Desktop: headline left, a big product pack shot on a turmeric circle right, the same bestseller row below.',
        },
        {
            name: 'Product card',
            recipe: 'Rounded card (theme radius) on surface. 1:1 image with a "Bestseller" or "New" badge top-left in rani pink. Name (2 lines max, weight 600), weight or size in muted text, rating stars with count, then the price row: current price big and bold, MRP struck through, "% off" in pink. A full-width chilli-red "Add" button that turns into a quantity stepper after adding.',
        },
        {
            name: 'Trust strip',
            recipe: 'A band of 4 icon-and-label items (COD available, Free shipping over ₹499, 7-day returns, Made in India) on the deeper surface colour, 2x2 on mobile, a single row on desktop. Icons are chunky line icons in tamarind brown; labels in 14px weight 600.',
        },
        {
            name: 'Combo deal band',
            recipe: 'A full-bleed chilli-red section with white text: headline "Make it a combo", 2–3 combo cards on cream with a pink "Save ₹200" sticker each, showing the included products as overlapping circles, the combo price and a white button with red text.',
        },
        {
            name: 'Reviews wall',
            recipe: 'Masonry of customer review cards (cream with caramel border), each with stars, a short quote in weight 500, name and city ("Priya, Pune"), and an optional customer photo. Header shows the average rating big with the total count. Only real reviews from the reviews plugin; never invent them.',
        },
    ],
});
